import { useQuery } from '@tanstack/react-query'
import { apiFetch, getCallerAgentId, hasLiveApi } from '@/api/client'
import { agentNodeToAccount, type AgentNode } from '@/api/map/agent'
import { decodeClaims, getAccessToken, isDemoSession } from '@/auth/oidc'
import * as api from '@/data/api'
import type { Account } from '@/data/types'
import { keys } from './keys'

export interface SessionView {
  session: Awaited<ReturnType<typeof api.getSession>>
  account: Account
}

async function loadSession(): Promise<SessionView> {
  if (hasLiveApi()) {
    const id = getCallerAgentId()!
    const node = await apiFetch<AgentNode>(`/v1/agents/${id}`)
    const account = agentNodeToAccount(node)
    const who = decodeClaims(getAccessToken())
    return {
      session: {
        accountId: account.id,
        username: who?.preferredUsername ?? account.username,
        displayName: account.displayName,
        role: account.role,
        scopes: [
          ...(who?.roles.includes('ops') ? (['ops'] as const) : []),
          ...(who?.roles.includes('risk-desk') ? (['risk-desk'] as const) : []),
        ],
      },
      account,
    }
  }

  if (!isDemoSession() && getAccessToken() && !getCallerAgentId()) {
    throw new Error(
      'Signed in but no agentId on the token — bind agentId on the Keycloak user (npm run keycloak:seed)',
    )
  }

  const session = await api.getSession()
  const account = await api.getAccount(session.accountId)
  if (!account) throw new Error('Your account could not be loaded')
  return { session, account }
}

export function useSession() {
  return useQuery({
    queryKey: keys.session,
    queryFn: loadSession,
    staleTime: 60_000,
  })
}
